import React from "react";
import style from "./../style.module.css";
import { Input, Button, Textarea } from "@my-scoot/component-library-legacy";
import { is_empty } from "utils/validations";
import { api, dataProvider } from "data";
import constants from "constants/constants";
import { useNotifications } from "utils/hooks";
import { refreshIframe, validateData } from "./CreatorMiscellaneousData/Utils";
import { WebpageCreationContext } from "ui/pages/TemplatePreview";
import classnames from "classnames";
import {
  MiscellaneousConfig,
  SIDEBAR_SECTIONS,
  SIDEBAR_KEYS,
  SECTIONKEY_TO_MISC_KEY,
} from "ui/pages/TemplatePreview/constants";
import useSetMissceleneous from "./common/useSetMissceleneous";
import { handleErrors } from "./common/validator";
import useTemplatePreview from "ui/pages/TemplatePreview/hooks/useTemplatePreview";
import { isRequestSuccessful } from "utils/Utils";
import { creator_miscellaneous_data_types } from "features/Common/modules/CreatorData/CreatorData.constants";
import SectionVisibilityToggle from "./hideShow";
import { handleVisibilityToggle } from "../utils";
import useVisibilityMutations from "../useVisibilityMutations.hook";

const formData = {
  heading: {
    key: "heading",
    label: "Heading",
  },
  description: {
    key: "description",
    label: "Description",
  },
};

export const Blogs = ({ disableAllEdits }) => {
  const { notify } = useNotifications();
  const { miscellaneous_data, fetchMiscellaneousData, set_miscellaneous_data } =
    React.useContext(WebpageCreationContext);
  const { pushNotif } = useTemplatePreview();
  const [section_name, set_section_name] = React.useState("Blogs");
  const [uuid, set_uuid] = React.useState(null);
  const [processing, set_processing] = React.useState(false);
  const [data, set_data] = React.useState({
    heading: "Recent Blog Posts",
  });
  const [error_object, set_error_object] = React.useState({});
  const [visible, set_visible] = React.useState(true);

  // Mutations for visibility toggle
  const { createMutation, updateMutation } = useVisibilityMutations();

  React.useEffect(() => {
    assignData();
  }, []);

  React.useEffect(() => {
    // Taking 0th element of miscellaneous_data - blogs as latest to update
    if (!is_empty(miscellaneous_data?.blogs)) {
      const [blogsData] = miscellaneous_data.blogs;
      if (blogsData?.uuid) {
        set_uuid(blogsData?.uuid);
      }
    }
  }, [miscellaneous_data]);

  useSetMissceleneous({
    listeners: [data, section_name, uuid, visible],
    data: {
      ...miscellaneous_data,
      blogs: [
        {
          ...(uuid && {
            uuid,
          }),
          data: {
            section_name,
            visible,
            data: {
              heading: data?.heading,
              description: data?.description,
            },
          },
        },
      ],
    },
    updateHandler: {
      blogs: {
        handleSave: () => handleSave(),
      },
    },
  });

  const assignData = () => {
    try {
      if (is_empty(miscellaneous_data?.blogs)) return;
      let temp = miscellaneous_data?.blogs[0];
      set_data({ ...temp?.data?.data });
      set_section_name(temp?.data?.section_name || "Blogs");
      set_uuid(temp?.uuid);

      // SAFETY NET: default visible = true
      set_visible(temp?.data?.visible ?? true);
    } catch (err) {
      notify(
        err?.body?.message ? err?.body?.message : constants.error_message,
        "error"
      );
    }
  };

  const handleSave = async () => {
    try {
      if (processing) return;
      set_processing(true);

      const newData = {
        heading: data?.heading,
      };
      const error_obj = validateData(newData, "Required");

      if (!is_empty(error_obj)) {
        set_error_object(Object.assign(error_object, error_obj));
        return notify("Invalid form values", "error");
      }

      let payload, api_resource;
      let temp = data;
      if (temp?.draft) {
        delete temp.draft;
      }
      let api_uuid = uuid;
      if (!is_empty(miscellaneous_data?.blogs)) {
        const [blogsData] = miscellaneous_data.blogs;
        if (blogsData?.uuid) {
          api_uuid = blogsData?.uuid;
        }
      }
      if (is_empty(api_uuid)) {
        api_resource = api.creator_miscellaneous_data.create;
        payload = {
          type: creator_miscellaneous_data_types.BLOGS,
          data: { section_name, visible, data: temp },
        };
      } else {
        api_resource = api.creator_miscellaneous_data.update;
        payload = {
          uuid: api_uuid,
          updated_values: {
            data: { section_name, visible, data: temp },
          },
        };
      }
      const status = await dataProvider.custom_request(
        api_resource,
        "POST",
        payload
      );
      if (isRequestSuccessful(status.status)) {
        refreshIframe();
        set_data({ ...temp });
        set_error_object({});
        if (is_empty(uuid)) {
          fetchMiscellaneousData({
            type: creator_miscellaneous_data_types.BLOGS,
          });
        } else {
          let context_data = miscellaneous_data.blogs;
          context_data[0].data.data = temp;
          set_miscellaneous_data({
            ...miscellaneous_data,
            blogs: context_data,
          });
        }
      }
      pushNotif(status?.message);
    } catch (err) {
      notify(
        err?.body?.message ? err?.body?.message : constants.error_message,
        "error"
      );
    } finally {
      set_processing(false);
    }
  };

  const updateData = (value, key) => {
    let temp = data;
    temp[key] = value;
    set_data({ ...temp });
  };

  const onToggleVisibility = () =>
    handleVisibilityToggle({
      currentVisible: visible,
      setVisible: set_visible,
      miscellaneousData: miscellaneous_data,
      sectionKey: SECTIONKEY_TO_MISC_KEY[SIDEBAR_KEYS.BLOGS],
      sectionType: creator_miscellaneous_data_types.BLOGS,
      defaultSectionName: SIDEBAR_SECTIONS[SIDEBAR_KEYS.BLOGS].title,
      fetchMiscellaneousData,
      set_miscellaneous_data,
      createMutation: createMutation,
      updateMutation: updateMutation,
    });

  return (
    <div>
      <SectionVisibilityToggle
        visible={visible}
        disabled={disableAllEdits}
        onToggle={onToggleVisibility}
      />

      <div className={style.label}>
        What would you like to call this section?
      </div>

      <div className={`mt-3 mb-1`}>
        <Input
          fullWidth
          value={section_name}
          error={error_object.sectionNameLength}
          helperText={error_object.sectionNameLength}
          inputProps={{
            maxLength: MiscellaneousConfig.maxShortTitleLenght,
          }}
          onChange={(e) => {
            let value = e.target.value;

            if (value.length >= MiscellaneousConfig.maxShortTitleLenght) {
              set_error_object({
                ...error_object,
                sectionNameLength: `Maximum field length of ${MiscellaneousConfig.maxShortTitleLenght} reached.`,
              });
            } else {
              set_error_object({ ...error_object, sectionNameLength: false });
            }
            set_section_name(value);
          }}
          disabled={disableAllEdits}
        />
      </div>

      <div className={`${style.label} ${style.required} mt-3 mb-1`}>
        {formData.heading.label}
      </div>
      <Input
        error={error_object?.heading}
        helperText={error_object?.heading}
        placeholder="Eg: Latest from the blog"
        value={data?.heading}
        className={style.placeholder}
        fullWidth
        inputProps={{
          maxLength: MiscellaneousConfig.maxTitleLength,
        }}
        onChange={(e) => {
          let value = e.target.value;

          const error = handleErrors(
            error_object,
            formData.heading.key,
            value,
            MiscellaneousConfig.maxTitleLength
          );
          set_error_object(error);
          updateData(value, formData.heading.key);
        }}
      />

      <div className={`${style.label} mt-3 mb-1`}>
        {formData.description.label}
      </div>
      <Textarea
        error={error_object?.description}
        helperText={error_object?.description}
        placeholder="Tip: use this space to describe this section"
        value={data?.description}
        rows="4"
        customClasses={{
          wrapper: style.placeholder,
        }}
        maxLength={MiscellaneousConfig.maxDescLength}
        onChange={(e) => {
          let value = e.target.value;

          const error = handleErrors(
            error_object,
            formData.description.key,
            value,
            MiscellaneousConfig.maxDescLength,
            false
          );
          set_error_object(error);
          updateData(value, formData.description.key);
        }}
        minWidth="100%"
      />

      <Button
        variant="contained"
        color="primary"
        disableElevation
        disabled={processing || disableAllEdits}
        loading={processing}
        onClick={handleSave}
        className={classnames(style.nextSectionPrompt, "mt-4 mb-3")}
      >
        Save & next
      </Button>
    </div>
  );
};
